import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet } from "@tanstack/react-router";
import { z } from "zod";
import { AuthGate } from "./components/AuthGate.tsx";
import { AppShell } from "./components/AppShell.tsx";
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "./pages/auth.tsx";
import { AcceptInvitePage } from "./pages/invite.tsx";
import { OnboardingPage } from "./pages/onboarding.tsx";
import { HomePage } from "./pages/home.tsx";
import { MenusPage } from "./pages/menus.tsx";
import { MenuLayout, MenuContentPage } from "./pages/menu-content.tsx";
import { MenuTranslationsPage } from "./pages/menu-translations.tsx";
import { MenuPublishPage } from "./pages/menu-publish.tsx";
import { MenuSettingsPage } from "./pages/menu-settings.tsx";
import { NewImportPage, ImportJobPage } from "./pages/imports.tsx";
import { RestaurantPage } from "./pages/restaurant.tsx";
import { TablesPage } from "./pages/tables.tsx";
import { OrdersPage } from "./pages/orders.tsx";
import { KitchenSetupPage } from "./pages/kitchen-setup.tsx";
import { TeamPage } from "./pages/team.tsx";
import { BillingPage } from "./pages/billing.tsx";
import { SettingsPage } from "./pages/settings.tsx";
import { NotFound } from "./components/NotFound.tsx";

const rootRoute = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFound });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage, validateSearch: z.object({ redirect: z.string().optional(), error: z.string().max(64).optional() }) });
const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: SignupPage, validateSearch: z.object({ redirect: z.string().optional(), plan: z.enum(["pro", "business"]).optional().catch(undefined) }) });
const forgotRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forgot-password", component: ForgotPasswordPage });
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset-password", component: ResetPasswordPage, validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }) });
const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/verify-email", component: VerifyEmailPage });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invite/$token", component: AcceptInvitePage });

/** Authenticated area without the app chrome (onboarding, full-screen editor). */
const bareAuthRoute = createRoute({ getParentRoute: () => rootRoute, id: "bare", component: () => <AuthGate requireOrg={false}><Outlet /></AuthGate> });
const onboardingRoute = createRoute({ getParentRoute: () => bareAuthRoute, path: "/onboarding", component: OnboardingPage });
const editorAuthRoute = createRoute({ getParentRoute: () => rootRoute, id: "editor", component: () => <AuthGate requireOrg><Outlet /></AuthGate> });
const designRoute = createRoute({ getParentRoute: () => editorAuthRoute, path: "/menus/$menuId/design", component: lazyRouteComponent(() => import("./pages/design.tsx"), "DesignEditorPage") });
/** Full-screen kitchen display (no app chrome). */
const kitchenRoute = createRoute({
  getParentRoute: () => editorAuthRoute,
  path: "/kitchen",
  component: lazyRouteComponent(() => import("./pages/kitchen.tsx"), "KitchenPage"),
  validateSearch: z.object({ restaurantId: z.string().optional(), station: z.string().optional() }),
});
/** Full-screen live floor for service. */
const floorLiveRoute = createRoute({
  getParentRoute: () => editorAuthRoute,
  path: "/floor",
  component: lazyRouteComponent(() => import("./pages/floor-live.tsx"), "FloorLivePage"),
  validateSearch: z.object({ restaurantId: z.string().optional() }),
});
/** Full-screen POS for staff order entry. */
const posRoute = createRoute({
  getParentRoute: () => editorAuthRoute,
  path: "/pos",
  component: lazyRouteComponent(() => import("./pages/pos.tsx"), "PosPage"),
  validateSearch: z.object({ restaurantId: z.string().optional(), tableId: z.string().optional() }),
});

/** Platform admin (Dishfolio staff). Doesn't need an organization; AdminLayout checks the role. */
const adminAuthRoute = createRoute({ getParentRoute: () => rootRoute, id: "admin-auth", component: () => <AuthGate requireOrg={false}><Outlet /></AuthGate> });
const adminPage = (name: string) => lazyRouteComponent(() => import("./pages/admin.tsx"), name as never);
const adminRoute = createRoute({ getParentRoute: () => adminAuthRoute, path: "/admin", component: adminPage("AdminLayout") });
const adminOverviewRoute = createRoute({ getParentRoute: () => adminRoute, path: "/", component: adminPage("AdminOverviewPage") });
const adminUsersRoute = createRoute({ getParentRoute: () => adminRoute, path: "/users", component: adminPage("AdminUsersPage"), validateSearch: z.object({ q: z.string().optional() }) });
const adminOrgsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/organizations", component: adminPage("AdminOrganizationsPage") });
const adminTrafficRoute = createRoute({ getParentRoute: () => adminRoute, path: "/traffic", component: adminPage("AdminTrafficPage") });
const adminServerRoute = createRoute({ getParentRoute: () => adminRoute, path: "/server", component: adminPage("AdminServerPage") });
const adminFlagsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/flags", component: lazyRouteComponent(() => import("./pages/admin-flags.tsx"), "AdminFlagsPage") });

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "app", component: () => <AuthGate requireOrg><AppShell /></AuthGate> });
const homeRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: HomePage });
const menusRoute = createRoute({ getParentRoute: () => appRoute, path: "/menus", component: MenusPage });
const menuRoute = createRoute({ getParentRoute: () => appRoute, path: "/menus/$menuId", component: MenuLayout });
const menuContentRoute = createRoute({ getParentRoute: () => menuRoute, path: "/", component: MenuContentPage });
const menuTranslationsRoute = createRoute({ getParentRoute: () => menuRoute, path: "/translations", component: MenuTranslationsPage });
const menuPublishRoute = createRoute({ getParentRoute: () => menuRoute, path: "/publish", component: MenuPublishPage });
const menuAnalyticsRoute = createRoute({ getParentRoute: () => menuRoute, path: "/analytics", component: lazyRouteComponent(() => import("./pages/menu-analytics.tsx"), "MenuAnalyticsPage") });
const menuSettingsRoute = createRoute({ getParentRoute: () => menuRoute, path: "/settings", component: MenuSettingsPage });
const newImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/imports/new",
  component: NewImportPage,
  validateSearch: z.object({ type: z.enum(["website", "pdf"]).default("website"), restaurantId: z.string().optional(), menuId: z.string().optional() }),
});
const importJobRoute = createRoute({ getParentRoute: () => appRoute, path: "/imports/$jobId", component: ImportJobPage });
const restaurantRoute = createRoute({ getParentRoute: () => appRoute, path: "/restaurants/$restaurantId", component: RestaurantPage });
const tablesRoute = createRoute({ getParentRoute: () => appRoute, path: "/restaurants/$restaurantId/tables", component: TablesPage });
const kitchenSetupRoute = createRoute({ getParentRoute: () => appRoute, path: "/restaurants/$restaurantId/kitchen", component: KitchenSetupPage });
const floorEditorRoute = createRoute({ getParentRoute: () => appRoute, path: "/restaurants/$restaurantId/floor", component: lazyRouteComponent(() => import("./pages/floor-editor.tsx"), "FloorEditorPage") });
const ordersRoute = createRoute({ getParentRoute: () => appRoute, path: "/orders", component: OrdersPage, validateSearch: z.object({ restaurantId: z.string().optional() }) });
const teamRoute = createRoute({ getParentRoute: () => appRoute, path: "/team", component: TeamPage });
const billingRoute = createRoute({ getParentRoute: () => appRoute, path: "/billing", component: BillingPage, validateSearch: z.object({ checkout: z.string().optional() }) });
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: "/settings", component: SettingsPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  forgotRoute,
  resetRoute,
  verifyRoute,
  inviteRoute,
  bareAuthRoute.addChildren([onboardingRoute]),
  editorAuthRoute.addChildren([designRoute, kitchenRoute, posRoute, floorLiveRoute]),
  adminAuthRoute.addChildren([adminRoute.addChildren([adminOverviewRoute, adminUsersRoute, adminOrgsRoute, adminTrafficRoute, adminServerRoute, adminFlagsRoute])]),
  appRoute.addChildren([
    homeRoute,
    menusRoute,
    menuRoute.addChildren([menuContentRoute, menuTranslationsRoute, menuPublishRoute, menuAnalyticsRoute, menuSettingsRoute]),
    newImportRoute,
    importJobRoute,
    restaurantRoute,
    tablesRoute,
    kitchenSetupRoute,
    floorEditorRoute,
    ordersRoute,
    teamRoute,
    billingRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
